import React from 'react';
import Icon from '../../../components/openplay/OpenPlayIcon';
import AppImage from '../../../components/openplay/OpenPlayImage';

const EventCard = ({ event, onApply, onInquiry, onViewDetails, onShowImage, imageCount, variant = 'default' }) => {
  const maxParticipants = Number(event?.maxParticipants);
  const participantCount = Number(event?.participantCount);
  const hasCapacityLimit = Number.isFinite(maxParticipants) && maxParticipants > 0;
  const safeParticipantCount = Number.isFinite(participantCount) ? participantCount : 0;
  const isFull = hasCapacityLimit ? safeParticipantCount >= maxParticipants : false;
  const availableSpots = hasCapacityLimit ? maxParticipants - safeParticipantCount : 0;
  const showInquiry = typeof onInquiry === 'function';
  const eventType = String(event?.eventType || '').trim().toLowerCase();
  const showApply = ['open_game', 'lesson', 'training'].includes(eventType);
  const showDetails = showApply;
  const isModal = variant === 'modal';
  const description = String(event?.description || '').trim();
  const iconClassName = isModal ? 'text-foreground/70' : 'text-muted-foreground';
  const headerTitle = isModal ? event?.title : event?.organizationName;
  const headerImageSrc = String(event?.coverImageUrl || '') || (Array.isArray(event?.imageUrls) ? event.imageUrls[0] : '') || event?.organizationLogo;

  const getSportLabel = (sport) => {
    const labels = {
      soccer: '축구',
      basketball: '농구',
      baseball: '야구',
      volleyball: '배구',
      badminton: '배드민턴',
      tennis: '테니스',
    };
    return labels[sport] || sport;
  };

  return (
    <div
      className={`bg-card rounded-xl shadow-elevation-2 overflow-hidden ${
        isModal ? 'flex flex-col max-h-[85vh]' : ''
      }`}
    >
      <div className={`flex items-center gap-3 p-4 border-b border-border ${isModal ? 'shrink-0' : ''}`}>
        <button
          type="button"
          onClick={() => {
            if (typeof onShowImage === 'function') onShowImage();
          }}
          className={`relative w-12 h-12 rounded-full overflow-hidden bg-muted ${typeof onShowImage === 'function' ? 'cursor-pointer' : 'cursor-default'}`}
          aria-label="대표 이미지 크게 보기"
        >
          <AppImage src={headerImageSrc} alt={event?.title || event?.organizationLogoAlt} className="w-full h-full object-cover" />
          {Number.isFinite(Number(imageCount)) && Number(imageCount) >= 1 && (
            <span className="absolute bottom-0.5 right-0.5 px-1.5 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-semibold leading-none">
              {Number(imageCount)}장
            </span>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-foreground truncate">{headerTitle}</h3>
          <p className="text-sm text-muted-foreground caption">
            {event.distance}
            {!isModal && ` · ${getSportLabel(event.sportType)}`}
          </p>
        </div>
        {showDetails && (
          <button
            onClick={onViewDetails}
            className="flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground hover:bg-muted transition-smooth touch-target"
            aria-label="상세 정보"
            type="button"
          >
            <Icon name="ChevronRightIcon" size={20} className={iconClassName} />
          </button>
        )}
      </div>

      <div className={`p-4 space-y-3 ${isModal ? 'flex-1 overflow-y-auto' : ''}`}>
        {!isModal && <h4 className="text-lg font-semibold text-foreground">{event.title}</h4>}

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="CalendarIcon" size={16} className={iconClassName} />
            <span>
              {event.date} {event.time}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="MapPinIcon" size={16} className={iconClassName} />
            <span>{event.location}</span>
          </div>
          {showApply && (
            <>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="UserGroupIcon" size={16} className={iconClassName} />
                <span>
                  {safeParticipantCount}/{hasCapacityLimit ? maxParticipants : '-'}명 참가
                  {hasCapacityLimit && !isFull && ` · ${availableSpots}자리 남음`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="StarIcon" size={16} className={iconClassName} />
                <span>실력: {event.skillLevel}</span>
              </div>
            </>
          )}
        </div>

        {isModal && description && (
          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted-foreground caption">설명</p>
            <p className="text-sm text-foreground whitespace-pre-line break-words">{description}</p>
          </div>
        )}

        {showApply && (
          <div className="flex items-center gap-2">
            {isFull ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-error-foreground bg-error rounded-full">
                <Icon name="XCircleIcon" size={16} />
                마감
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-success-foreground bg-success rounded-full">
                <Icon name="CheckCircleIcon" size={16} />
                참가 가능
              </span>
            )}
          </div>
        )}
      </div>

      <div className={`flex gap-3 p-4 border-t border-border ${isModal ? 'shrink-0' : ''}`}>
        {showInquiry && (
          <button
            onClick={onInquiry}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-border text-foreground hover:bg-muted transition-smooth touch-target font-medium"
            type="button"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} />
            문의하기
          </button>
        )}
        {showApply && (
          <button
            onClick={onApply}
            disabled={isFull}
            className={`${showInquiry ? 'flex-1' : 'w-full'} flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-smooth touch-target font-medium ${
              isFull
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-primary-foreground hover:shadow-elevation-2 active:scale-97'
            }`}
            type="button"
          >
            <Icon name="PlusCircleIcon" size={20} />
            {isFull ? '마감됨' : '참가 신청'}
          </button>
        )}
      </div>
    </div>
  );
};

export default EventCard;
